// The agreement signature is persisted to the browser because Cash App,
// Amazon Pay, Stripe bank, card redirects and PayPal leave the page and come
// back with a full reload, and the return handler in ActionPaymentMethod still
// has to send the signature with the payment. Anything beyond that round trip
// is a leftover: it pre-fills the next visit to an agreement as already signed.
//
// So the saved copy carries the slug it was drawn for, is only read back on
// that agreement, and is dropped at startup unless this load is a payment
// provider sending the customer back to that same agreement.

export type SavedAgreementSignature = {
  sign_base64?: string;
  slug?: string;
};

// Query params the providers append to the return URL. `method=` alone is not
// enough: it is our own param and is also used for the disclosure deep link.
const RETURN_MARKERS = [
  "payment_intent",
  "setup_intent",
  "approval_token_id",
  "approval_session_id",
];

const AGREEMENT_PATH = /^\/agreement\/([^/]+)\/?$/;

export const agreementSlugFromPath = (pathname: string): string | null => {
  const match = AGREEMENT_PATH.exec(pathname);
  if (!match) return null;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

export const isPaymentReturn = (search: string): boolean => {
  const params = new URLSearchParams(search);
  if (RETURN_MARKERS.some((key) => params.has(key))) return true;
  // PayPal order approval comes back with token + status.
  return params.has("token") && params.has("status");
};

// A copy saved before slugs were recorded has no slug. It is still kept on a
// payment return to an agreement, so a customer mid-redirect during a deploy
// does not come back to a payment without a signature; any other load drops it.
export const shouldKeepAgreementSignature = (
  saved: SavedAgreementSignature | undefined | null,
  location: { pathname: string; search: string }
): boolean => {
  if (!saved) return false;
  const slug = agreementSlugFromPath(location.pathname);
  if (!slug || !isPaymentReturn(location.search)) return false;
  return !saved.slug || saved.slug === slug;
};

// Only an unslugged copy that survived the startup check above can reach this
// without a slug, so it belongs to the agreement being returned to.
export const isSavedForAgreement = (
  saved: SavedAgreementSignature | undefined | null,
  slug: string | undefined
): boolean => !!saved && !!slug && (!saved.slug || saved.slug === slug);

export const signatureForAgreement = (
  saved: SavedAgreementSignature | undefined | null,
  slug: string | undefined
): string =>
  isSavedForAgreement(saved, slug) ? saved?.sign_base64 || "" : "";

// The PayPal order slot is shared with payment links, invoices, make-payment,
// subscriptions and failed payments, whose PayPal returns read it after the
// redirect. Only the agreement flow's order carries the signature, so only
// that one is managed here.
export const isAgreementPayPalOrder = (order: any): boolean =>
  !!order &&
  typeof order === "object" &&
  ("sign_base64" in order || "slug" in order);
